<template>
		<section>
			<h3>Products</h3>
			<div v-if="getSubscriptionsStatus.error" class="notice warning">
				We're having trouble retrieving subscriptions.
				<span class="is-support">
					Feel free to write us a
					<a href="mailto:support@initialstate.com?subject=Trouble Loading Subscriptions">nasty email</a>
					about your experience.
				</span>
			</div>
			<div class="list-group">
				<div class="cards row separated separated-extra relative">
					<card-placeholder v-if="getSubscriptionsStatus.loading"></card-placeholder>
					<div class="column flexible card card-primary" v-else-if="getSubscriptionsStatus.success">
						<section class="column flexible">
							<header class="row rigid">
								<h1 class="flexible">
									TekDrive
								</h1>
								<div class="rigid circle">
									<i class="far fa-folder jumbo"></i>
								</div>
							</header>
							<!-- static sub(s) prepaid or trial -->
							<div class="column separated-extra">
								<div v-if="staticDriveSubs.length" class="flexible column separated-extra">
									<div v-for="(driveSub, index) in staticDriveSubs" :key="index" class="sub-details row separated rigid centering">
										<div class="flexible">
											<router-link title="View TekDrive Subscription" :to="{ path: 'subscriptions/drive/' + driveSub.id }" class="rigid trailing-off">
												{{driveSub.plan.name}}
											</router-link>
											<ul class="small rigid gray-5">
												<li v-if="driveSub.expiry">Ends {{driveSub.expiry | date('medium')}}</li>
												<li v-if="driveSub.isFreePromo">Free</li>
												<li>{{driveSub.type}}</li>
											</ul>
										</div>
									</div>
								</div>
								<!-- paid sub -->
								<div class="flexible column separated" v-if="driveSub">
									<div class="flexible">
										<router-link title="View TekDrive Subscription" to="subscriptions/drive/" class="rigid trailing-off">
											{{driveSub.plan.name}}
										</router-link>
										<ul class="small rigid gray-5 card-content-footnote">
											<li>
												<span v-if="driveSub.renews">Renews</span>
												<span v-else>Ends</span>
												{{driveSub.expiry | date('medium')}}</li>
											<li>{{driveSub.type}}</li>
										</ul>
									</div>
								</div>
								<!-- no sub -->
								<div class="flexible column separated" v-if="driveSubs.length === 0">
									<div class="rigid gray-7 card-content">
										No subscription
									</div>
								</div>
							</div>
						</section>

						<footer class="row rigid separated">
							<router-link v-if="driveSub" class="button flexible" to="/subscriptions/drive">
								<span v-if="driveSub.isPrepaid">
									View
								</span>
								<span v-else>
									Manage
								</span>
							</router-link>
							<router-link v-else class="button flexible" to="/subscriptions/drive">Subscribe</router-link>
							<a class="button flexible" v-if="driveSub" target="_blank" :href="driveUrl">Launch</a>
							<a v-else class="button flexible" href="http://www.tekcloud.com/tekdrive" target="_blank">Learn More</a>
						</footer>
					</div>

					<card-placeholder v-if="getSubscriptionsStatus.loading"></card-placeholder>
					<div class="column flexible card card-primary" v-else-if="getSubscriptionsStatus.success">
						<section class="column flexible">
							<header class="row rigid">
								<h1 class="flexible">
									TekScope
								</h1>
								<div class="rigid circle">
									<i class="fas fa-laptop jumbo"></i>
								</div>
							</header>
							<!-- static sub(s) prepaid or trial -->
							<div class="column separated-extra">
								<div v-if="scopeSubs.length" class="flexible column separated-extra">
									<div v-for="(scopeSub, index) in scopeSubs" :key="index" class="sub-details row separated rigid centering">
										<div class="flexible">
											<router-link title="View TekScope Subscription" :to="{ path: 'subscriptions/scope/' + scopeSub.id }" class="rigid trailing-off">
												<div class="trailing-off" :title="scopeSub.plan.currentAddOnsDisplayText" v-if="scopeSub.plan.currentAddOnsDisplayText && scopeSub.plan.currentAddOnsDisplayText.length > 0">
													{{scopeSub.plan.currentAddOnsDisplayText}}
												</div>
												<span v-else>{{scopeSub.plan.name}}</span>
											</router-link>
											<ul class="small rigid gray-5">
												<li>
													<span v-if="scopeSub.renews">Renews</span>
													<span v-else>Ends</span>
													{{scopeSub.expiry | date('medium')}}</li>
												<li>{{scopeSub.type}}</li>
											</ul>
										</div>
									</div>
								</div>
								<!-- no sub -->
								<div class="flexible column separated" v-if="scopeSubs.length === 0">
									<div class="rigid gray-7 card-content">
										No subscription
									</div>
								</div>
							</div>
						</section>


						<footer class="row rigid separated">
							<router-link class="button flexible" to="/subscriptions/scope">
								Add
							</router-link>

							<dropdown-menu v-if="scopeSubs.length" :position="'center'" class="button flexible" :menuText="'More'">
								<a target="_blank" :href="installTekScopeUrl">Install TekScope</a>
								<a target="_blank" :href="updateLicenseUrl">Update License</a>
								<a target="_blank" :href="packagesUrl">Packages</a>
								<a target="_blank" :href="helpUrl">Help</a>
							</dropdown-menu>
							<!-- update with new tekcloud links -->
							<a class="button flexible" v-else href="https://www.tekcloud.com/tekscope" target="_blank">Learn More</a>
						</footer>
					</div>
					<spinner-new v-if="getSubscriptionsStatus.loading"></spinner-new>
				</div>
			</div>
		</section>
</template>

<script>
import SpinnerNew from '../components/SpinnerNew';
import SettingLink from '../components/SettingLink';
import SettingExpand from '../components/SettingExpand';
import DropdownMenu from '@/components/DropdownMenu';
import CardPlaceholder from '@/components/CardPlaceholder';
import { mapGetters, mapState } from 'vuex';
import date from '../filters/date';

export default {
	name: 'Subscriptions',
	data(){
		return {
			collapse: false
		};
	},
	filters: {
		date
	},
	computed: {
		...mapGetters([
			'account',
			'roles',
			'plans',
		]),
		...mapGetters('subscription',{
			subscriptions: 'subscriptions',
			getSubscriptionsStatus: 'getSubscriptionsStatus',
			changeSubscriptionStatus: 'changeSubscriptionStatus',
			iotSub: 'iotSubscription',
		}),
		...mapGetters({
			'driveSubs' : 'drive/driveSubscriptions',
			'driveSub' : 'drive/driveSubscription',
			'staticDriveSubs' : 'drive/staticDriveSubs',
			'scopeSubs': 'scope/scopeSubscriptions',
			'helpUrl' : 'auth/scopeHelpUrl',
			'packagesUrl' : 'auth/scopePackagesUrl',
			'updateLicenseUrl' : 'auth/updateLicenseUrl',
			'installTekScopeUrl' : 'auth/installTekScopeUrl',
			'driveUrl' : 'auth/driveAppUrl',
			'scopeUrl' : 'auth/scopeAppUrl',
		})
	},
	methods: {
		handleFunctionCall(fn) {
			this[fn]();
		},
		getSubName(plan) {
			return plan.name + ' ' + plan.interval;
		},
		setSubActive(id) {
			return id && this.$route.query && this.$route.query.planId === id;
		},
		collapseSetting(){
			this.collapse = true;
		},
		resetState() {
			this.$store.dispatch('subscription/RESET_CHANGE_SUBSCRIPTION_STATUS');
		}
	},
	components: {
		SpinnerNew,
		SettingLink,
		SettingExpand,
		DropdownMenu,
		CardPlaceholder
	},
	created () {
		this.$store.dispatch('subscription/RESET_CHANGE_SUBSCRIPTION_STATUS');
		this.$store.dispatch('subscription/SET_DRIVE_REDIRECT', false);
		this.$store.dispatch('subscription/SET_SCOPE_REDIRECT', false);
		this.$store.dispatch('subscription/GET_TEK_SUBSCRIPTIONS_REQUEST');
	}
};
</script>

<style scoped>
	.sub-details:first-child .sub-name {
		max-width: 225px;
	}

	.circle {
		background: #EBF6FF;
		height: 4rem;
		width: 4rem;
		border-radius: 50%;
		overflow: hidden;
		position: relative;
	}

	.dark .circle {
		background: #243044;
	}

	.circle > i {
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		color: #7CB9EE;
		bottom: 0;
		font-size: 3rem;
		text-align: center;
	}

	.circle > i.fa-laptop {
		font-size: 2.5rem;
		top: 20px;
	}
	.circle > i.fa-folder {
		top: 18px;
	}
</style>



// WEBPACK FOOTER //
// ./src/views/Subscriptions.vue